import { Button, Card, Descriptions, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type RunDetail = {
  id: string;
  status: string;
  solver_status?: string | null;
  progress_message?: string | null;
  error_message?: string | null;
  runtime_ms?: number | null;
  events?: Array<{ event_type: string; message?: string; created_at?: string }>;
  scenarios?: Array<{ id: string; name: string; status: string; hard_conflict_count?: number }>;
};

export default function SmartTimetableRunPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ['education-smart-tt-run', id],
    enabled: Boolean(id),
    refetchInterval: (q) => {
      const status = q.state.data?.data?.status;
      return status === 'pending' || status === 'compiling' || status === 'running' ? 2000 : false;
    },
    queryFn: () => api.get<RunDetail>(`/education/timetable/smart/runs/${id}`),
  });
  const run = query.data?.data;

  return (
    <EducationPageChrome
      title={t('generationRun', { defaultValue: 'Generation run' })}
      description={t('generationRunIntro', {
        defaultValue: 'Live solver status. Progress is real job state — not a fake percentage.',
      })}
      tags={run ? <Tag>{run.status}</Tag> : undefined}
      extra={<Button onClick={() => navigate('/education/timetable/smart/scenarios')}>{t('scenarios')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small" loading={query.isLoading}>
          <Descriptions size="small" column={1}>
            <Descriptions.Item label="ID">{run?.id}</Descriptions.Item>
            <Descriptions.Item label={t('status')}>{run?.status}</Descriptions.Item>
            <Descriptions.Item label={t('solverStatus', { defaultValue: 'Solver' })}>
              {run?.solver_status || '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('progress', { defaultValue: 'Progress' })}>
              {run?.progress_message || '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('runtimeMs', { defaultValue: 'Runtime ms' })}>
              {run?.runtime_ms ?? '—'}
            </Descriptions.Item>
            {run?.error_message ? (
              <Descriptions.Item label={t('error', { defaultValue: 'Error' })}>{run.error_message}</Descriptions.Item>
            ) : null}
          </Descriptions>
        </Card>
        <Card size="small" title={t('events', { defaultValue: 'Events' })}>
          <Table
            rowKey={(_, i) => String(i)}
            size="small"
            pagination={false}
            dataSource={run?.events || []}
            columns={[
              { title: t('type', { defaultValue: 'Type' }), dataIndex: 'event_type', width: 140 },
              { title: t('message', { defaultValue: 'Message' }), dataIndex: 'message' },
              {
                title: t('at', { defaultValue: 'At' }),
                dataIndex: 'created_at',
                width: 180,
                render: (v?: string) => v || '—',
              },
            ]}
          />
        </Card>
        <Card size="small" title={t('scenarios')}>
          <Table
            rowKey="id"
            size="small"
            dataSource={run?.scenarios || []}
            onRow={(row) => ({
              onClick: () => navigate(`/education/timetable/smart/scenarios/${row.id}`),
              style: { cursor: 'pointer' },
            })}
            columns={[
              { title: t('name'), dataIndex: 'name' },
              { title: t('status'), dataIndex: 'status', render: (v: string) => <Tag>{v}</Tag> },
              { title: t('hardConflicts', { defaultValue: 'Hard conflicts' }), dataIndex: 'hard_conflict_count' },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
